import React, { FC } from 'react';
import Image from 'next/image';
import { GenExternalLink } from '@/components';
import { isERCBoxSkipRequested } from '@/lib/api/subscriptionManager/helpers/scheduledItemHelpers';
import { TypeSubEngineInstallment } from '@/lib/api/subscriptionManager/types';
import { generateZendeskLink } from '@/utils/zendesk';
import interpolate from '@/utils/translations/interpolate';
import { TypeAccountSettingsPageTranslations } from '@/utils/translations/types/TypeAccountSettingsPageTranslations';
import { _getBillingDate, _getFormattedScheduledDate } from '../utils';
import { useGetProductByPlaygeniusProductAnchor } from '@/lib/contentfulHooks';

type NextBoxDetailsProps = {
  t: TypeAccountSettingsPageTranslations;
  locale: string | undefined;
  nextUnprocessedItem: TypeSubEngineInstallment;
};

const NextBoxShippingAndBilling: FC<NextBoxDetailsProps> = ({
  t,
  locale,
  nextUnprocessedItem,
}) => {
  const formattedScheduledDate = _getFormattedScheduledDate({
    scheduled_date: nextUnprocessedItem.scheduled_date,
    locale,
  });
  return (
    <>
      <p className="mb-1">{`${t.shipDateLabel} ${formattedScheduledDate}`}</p>
      <p>{`${t.billDateLabel} ${formattedScheduledDate}`}</p>
    </>
  );
};

const NextBoxSkippedMessaging: FC<NextBoxDetailsProps> = ({
  t,
  locale,
  nextUnprocessedItem,
}) => {
  const zendeskLinkUrl = generateZendeskLink(locale);

  const ZendeskLink = () => {
    return (
      <GenExternalLink
        href={zendeskLinkUrl}
        cta={t.contactUs}
        blankTarget
        noFollow
      />
    );
  };

  return (
    <p>
      {`${t.skippedKitMessagePart1} `}
      <ZendeskLink />
      {` ${interpolate(t.skippedKitMessagePart2, {
        skipDateCutoff: _getFormattedScheduledDate({
          scheduled_date: nextUnprocessedItem.scheduled_date,
          locale,
        }),
      })}`}
    </p>
  );
};

const NextBoxDetails: FC<NextBoxDetailsProps> = ({
  t,
  locale,
  nextUnprocessedItem,
}) => {
  const product = useGetProductByPlaygeniusProductAnchor(
    nextUnprocessedItem.product_anchor,
  );

  if (!product) {
    console.error(
      `Product not found for product anchor: ${nextUnprocessedItem.product_anchor}`,
    );
    return null;
  }

  const { title, shortDescription } = product.fields;

  return (
    <div className="flex flex-row">
      <Image
        src={`https:${product.fields.featuredImage.fields.Image.fields.file.url}`}
        alt={product.fields.title}
        width={125}
        height={125}
        className="h-[125px] w-[125px]"
      />
      <div>
        <p className="mb-2 font-bold">{title}</p>
        <p className="mb-4 font-bold">{shortDescription}</p>
        {isERCBoxSkipRequested(nextUnprocessedItem) ? (
          <NextBoxSkippedMessaging
            t={t}
            locale={locale}
            nextUnprocessedItem={nextUnprocessedItem}
          />
        ) : (
          <NextBoxShippingAndBilling
            t={t}
            locale={locale}
            nextUnprocessedItem={nextUnprocessedItem}
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(NextBoxDetails);
